import { ServerCog, GitBranchPlus, Rocket, Gauge, ArrowDown, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import React from "react";
import { ProviderTypes } from "@/types";
import { getProviderMeta } from "@/lib/provider/meta";
import { nonFalse } from "@/lib/utils";

interface StepProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const steps: StepProps[] = [
  {
    icon: <ServerCog size={56} />,
    title: "Connect Server",
    description: `Connect your server or automatically create a new one from ${ProviderTypes.map(
      getProviderMeta
    )
      .filter(nonFalse)
      .filter(p => !p.experimental && p.compute)
      .map(p => p.name)
      .join(", ")}`
  },
  {
    icon: <GitBranchPlus size={56} />,
    title: "Add Your App",
    description:
      "Link your GitHub repository. We support Next.js, React, Python, Go, and everything else with Docker Compose."
  },
  {
    icon: <Rocket size={56} />,
    title: "Deploy",
    description:
      "Click deploy or push to your Git branch. We handle build, deployment, HTTPS, and updates automatically."
  },
  {
    icon: <Gauge size={56} />,
    title: "Monitor",
    description:
      "View logs, check host health and status, manage hosts and backups through our intuitive dashboard."
  }
];

export const HowItWorks = () => {
  return (
    <section id="howItWorks" className="container text-center">
      <svg
        style={{ width: 0, height: 0, position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="icon-gradient" x2="1" y2="1">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="50%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
      </svg>

      <h2 className="text-3xl md:text-4xl font-bold ">
        From GitHub to{" "}
        <span className="gradient-light dark:gradient-dark">production in minutes</span>
      </h2>
      <p className="md:w-3/4 mx-auto mt-4 mb-8 text-xl text-muted-foreground">
        Connect a server, push your code, and your app is live. HTTPS, monitoring, and backups
        are included.
      </p>

      <div
        className="flex flex-col items-stretch lg:flex-row lg:items-stretch lg:justify-center gap-0"
        role="list"
        aria-label="How DollarDeploy works in four steps"
      >
        {steps.map(({ icon, title, description }: StepProps, index) => {
          const stepNumber = index + 1;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={title}>
              <div
                role="listitem"
                className="flex min-w-0 flex-1 flex-col lg:max-w-xs xl:max-w-sm"
                aria-label={`Step ${stepNumber} of ${steps.length}: ${title}`}
              >
                <Card className="group relative h-full bg-muted/60 border pt-6">
                  <span
                    className="absolute left-3 top-3 text-4xl font-bold text-muted-foreground/45 transition-colors group-hover:text-secondary-foreground/70 md:text-5xl md:left-4 md:top-3"
                    aria-hidden="true"
                  >
                    {stepNumber}
                  </span>
                  <CardHeader className="relative z-10">
                    <CardTitle className="text-xl grid gap-4 place-items-center">
                      <div className="text-primary [&_svg]:transition-all [&_svg]:[stroke-width:4] md:[&_svg]:[stroke-width:2] md:group-hover:[&_svg]:[stroke-width:4]">
                        {icon}
                      </div>
                      {title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-muted-foreground">{description}</CardContent>
                </Card>
              </div>

              {!isLast && (
                <div
                  className="flex shrink-0 items-center justify-center py-3 lg:px-2 lg:py-0"
                  aria-hidden="true"
                >
                  <ArrowDown
                    className="h-8 w-8 text-muted-foreground lg:hidden"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <ArrowRight
                    className="hidden h-8 w-8 text-muted-foreground lg:block"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};
